import { useEffect, useState, useRef } from "react"
import { reportService } from "../../services/reportService"
import { treeTypeService } from "../../services/treeTypeService"
import { MapPin, TreeType } from "../../types"

// ── Leaflet loaded via CDN in index.html ──────────────────────────────────────
declare const L: any

// ── Create a colored SVG circle marker ───────────────────────────────────────
function createColorIcon(color: string, count: number) {
  const size = count > 1000 ? 38 : count > 100 ? 32 : 26
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" fill-opacity="0.9" stroke="white" stroke-width="2"/>
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="transparent" stroke="${color}" stroke-width="1" stroke-opacity="0.4"/>
    </svg>
  `
  return L.divIcon({
    html: `<div style="filter: drop-shadow(0 2px 6px ${color}80)">${svg}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// ── Popup HTML ────────────────────────────────────────────────────────────────
function buildPopup(pin: MapPin): string {
  const trees = pin.trees
    .map(t => `
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
        <span style="width:8px;height:8px;border-radius:50%;background:${t.color_hex};flex-shrink:0"></span>
        <span style="color:#d1d5db;font-size:11px">${t.name}</span>
        <span style="color:#10b981;font-size:11px;font-weight:700;margin-left:auto">${Number(t.count).toLocaleString()}</span>
      </div>
    `).join("")

  return `
    <div style="font-family:system-ui,sans-serif;min-width:200px;background:#1a1a1a;border-radius:12px;overflow:hidden">
      <div style="background:${pin.primary_color}18;border-bottom:1px solid ${pin.primary_color}30;padding:12px 14px">
        <div style="font-size:10px;color:${pin.primary_color};font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:4px">
          ${pin.farmer_code}
        </div>
        <div style="font-size:14px;color:#fff;font-weight:800">${pin.farmer_name}</div>
        <div style="font-size:11px;color:#9ca3af;margin-top:2px">${pin.region}</div>
      </div>
      <div style="padding:12px 14px">
        <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:8px">Tree Inventory</div>
        ${trees}
        <div style="border-top:1px solid #ffffff10;margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;align-items:center">
          <span style="color:#6b7280;font-size:11px">Total</span>
          <span style="color:#10b981;font-size:16px;font-weight:900">${Number(pin.total_trees).toLocaleString()}</span>
        </div>
      </div>
    </div>
  `
}


export default function AdminMapView() {
  const mapRef        = useRef<any>(null)
  const markersRef    = useRef<any[]>([])
  const mapElRef      = useRef<HTMLDivElement>(null)

  const [pins,       setPins]       = useState<MapPin[]>([])
  const [treeTypes,  setTreeTypes]  = useState<TreeType[]>([])
  const [loading,    setLoading]    = useState(true)
  const [treeTypeId, setTreeTypeId] = useState<number | undefined>()
  const [selected,   setSelected]   = useState<MapPin | null>(null)
  const [search,     setSearch]     = useState("")

  useEffect(() => {
    if (mapRef.current || !mapElRef.current) return

    mapRef.current = L.map(mapElRef.current, {
      center: [-6.3690, 34.8888], 
      zoom: 6,
      zoomControl: false,
    })

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© InspireHub Limited",
      maxZoom: 19,
    }).addTo(mapRef.current)

    L.control.zoom({ position: "bottomright" }).addTo(mapRef.current)

    return () => {
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  // ── Load tree types ─────────────────────────────────────────────────────────
  useEffect(() => {
    treeTypeService.getAll().then(setTreeTypes).catch(() => {})
  }, [])

  // ── Load pins ───────────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(true)
    reportService.getMapPins({ tree_type_id: treeTypeId })
      .then(setPins)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [treeTypeId])

  // ── Render markers ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return

    // Clear old markers
    markersRef.current.forEach(m => m.remove())
    markersRef.current = []

    const filtered = pins.filter(p =>
      !search || p.farmer_name.toLowerCase().includes(search.toLowerCase()) ||
      p.farmer_code.toLowerCase().includes(search.toLowerCase()) ||
      p.region.toLowerCase().includes(search.toLowerCase())
    )

    filtered.forEach(pin => {
      const marker = L.marker([pin.latitude, pin.longitude], {
        icon: createColorIcon(pin.primary_color, pin.total_trees),
      })

      marker.bindPopup(buildPopup(pin), {
        maxWidth: 260,
        className: "akm-popup",
      })

      marker.on("click", () => setSelected(pin))
      marker.on("popupclose", () => setSelected(null))

      marker.addTo(mapRef.current)
      markersRef.current.push(marker)
    })

    // Fit bounds if markers exist
    if (filtered.length > 0 && !search) {
      const group = L.featureGroup(markersRef.current)
      mapRef.current.fitBounds(group.getBounds().pad(0.1))
    }
  }, [pins, search])

  const filtered = pins.filter(p =>
    !search || p.farmer_name.toLowerCase().includes(search.toLowerCase()) ||
    p.farmer_code.toLowerCase().includes(search.toLowerCase()) ||
    p.region.toLowerCase().includes(search.toLowerCase())
  )

  const flyTo = (pin: MapPin) => {
    mapRef.current?.flyTo([pin.latitude, pin.longitude], 14, { duration: 1 })
    setSelected(pin)
  }

  return (
    <div className="flex gap-4 h-[calc(100vh-130px)]">

      {/* ── Sidebar panel ── */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-3">

        {/* Search */}
        <div className="relative">
          <svg className="w-4 h-4 text-neutral-600 absolute left-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search farmers..."
            className="w-full bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.14] focus:border-emerald-500/40 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm outline-none placeholder:text-neutral-600 transition-colors"
          />
        </div>

        {/* Tree type filter */}
        <select
          value={treeTypeId ?? ""}
          onChange={e => setTreeTypeId(e.target.value ? Number(e.target.value) : undefined)}
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-neutral-300 outline-none cursor-pointer"
        >
          <option value="">All tree types</option>
          {treeTypes.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>

        {/* Stats bar */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl px-3 py-2.5 text-center">
            <p className="text-emerald-400 text-lg font-black">{filtered.length}</p>
            <p className="text-neutral-600 text-[10px] uppercase tracking-wider">Farms</p>
          </div>
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl px-3 py-2.5 text-center">
            <p className="text-emerald-400 text-lg font-black">
              {filtered.reduce((s, p) => s + Number(p.total_trees), 0).toLocaleString()}
            </p>
            <p className="text-neutral-600 text-[10px] uppercase tracking-wider">Trees</p>
          </div>
        </div>

        {/* Color legend */}
        {treeTypes.length > 0 && (
          <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3">
            <p className="text-neutral-600 text-[10px] uppercase tracking-widest mb-2">Legend</p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {treeTypes.map(t => (
                <div key={t.id} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: t.color_hex }} />
                  <span className="text-neutral-400 text-xs">{t.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Farmer list */}
        <div className="flex-1 bg-white/[0.03] border border-white/[0.07] rounded-xl overflow-hidden flex flex-col">
          <div className="px-3 py-2.5 border-b border-white/[0.06]">
            <p className="text-neutral-500 text-xs font-medium">
              {loading ? "Loading..." : `${filtered.length} locations`}
            </p>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32 gap-2 text-neutral-600">
                <svg className="w-4 h-4 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="text-xs">Loading pins...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-neutral-700 text-xs">
                No farms found
              </div>
            ) : (
              filtered.map(pin => (
                <button
                  key={pin.farmer_id}
                  onClick={() => flyTo(pin)}
                  className={`w-full text-left px-3 py-3 border-b border-white/[0.04] hover:bg-white/[0.04] transition-colors ${
                    selected?.farmer_id === pin.farmer_id ? "bg-emerald-500/5 border-l-2 border-l-emerald-500" : ""
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ring-2 ring-white/10"
                      style={{ backgroundColor: pin.primary_color }}
                    />
                    <div className="min-w-0">
                      <p className="text-white text-xs font-medium truncate">{pin.farmer_name}</p>
                      <p className="text-neutral-600 text-[10px] mt-0.5">{pin.region}</p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        {pin.trees.slice(0, 3).map((t, i) => (
                          <span
                            key={i}
                            className="text-[10px] px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: `${t.color_hex}20`, color: t.color_hex }}
                          >
                            {t.name} · {Number(t.count).toLocaleString()}
                          </span>
                        ))}
                      </div>
                    </div>
                    <span className="text-emerald-400 text-xs font-bold ml-auto flex-shrink-0">
                      {Number(pin.total_trees).toLocaleString()}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* ── Map ── */}
      <div className="flex-1 relative rounded-2xl overflow-hidden border border-white/[0.07]">
        <div ref={mapElRef} className="w-full h-full" />

        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 bg-[#111]/60 backdrop-blur-sm flex items-center justify-center z-[1000]">
            <div className="flex items-center gap-3 bg-[#1a1a1a] border border-white/10 rounded-2xl px-5 py-4">
              <svg className="w-5 h-5 animate-spin text-emerald-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <span className="text-white text-sm">Loading map data...</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && filtered.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-[1000] pointer-events-none">
            <div className="bg-[#1a1a1a]/90 border border-white/10 rounded-2xl px-6 py-5 text-center">
              <p className="text-neutral-400 text-sm">No farms with GPS data found</p>
              <p className="text-neutral-700 text-xs mt-1">Farmers need GPS coordinates to appear on the map</p>
            </div>
          </div>
        )}
      </div>

      {/* Leaflet popup custom styles injected */}
      <style>{`
        .akm-popup .leaflet-popup-content-wrapper {
          background: transparent;
          box-shadow: 0 25px 50px rgba(0,0,0,0.5);
          border-radius: 12px;
          padding: 0;
          border: 1px solid rgba(255,255,255,0.08);
        }
        .akm-popup .leaflet-popup-content {
          margin: 0;
          border-radius: 12px;
          overflow: hidden;
        }
        .akm-popup .leaflet-popup-tip-container {
          display: none;
        }
        .akm-popup .leaflet-popup-close-button {
          color: #9ca3af;
          font-size: 18px;
          top: 8px;
          right: 10px;
          z-index: 10;
        }
        .akm-popup .leaflet-popup-close-button:hover {
          color: white;
        }
      `}</style>
    </div>
  )
}